import type { ExternalLayerTimeFilter } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import type {
  AiDrawnAreaPayload,
  AiLayerPickPayload,
} from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  isFloodingBinsDemoLayer,
  isTingOutagesTnFloodsLayer,
  resolveTimeFilterField,
} from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/tennesseeNationalGuardLayers';
import { isUsaCurrentWildfiresLayer } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/usaCurrentWildfiresLayer';
import { WEATHER_FEATURESERVER_CATALOG } from 'components/MapPage/CorporateViewTabs/EventsTab/weatherFeatureServerCatalog';
import type { SelectedLayerForAI } from './prefetchLayerFeaturesForAI';
import { getAiMapScopeLabel } from './aiMapSpatialFilter';
import { sameExternalLayerForAi } from './buildAiLayerCatalog';

export type LayersRealtimeSelection = {
  source: 'Layers';
  id: string;
  selectedLayers: SelectedLayerForAI[];
};

const WEATHER_CATALOG_IDS = new Set(
  WEATHER_FEATURESERVER_CATALOG.map((entry) => entry.id)
);

export type MapLayerToShow = {
  kind: 'internal' | 'external';
  id: string;
};

/**
 * Catalog slugs are not in ArcGIS sync; only persisted `external_layers` UUIDs
 * are.
 */
export function isWeatherCatalogSlugId(id: string): boolean {
  return WEATHER_CATALOG_IDS.has(id);
}

/**
 * Resolve a picker selection to a map-syncable layer id. Weather catalog slugs
 * map to the live UUID when `catalogExternals` has the seeded row.
 */
export function resolveMapLayerId(
  layer: Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>,
  catalogExternals: Array<
    Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>
  > = []
): MapLayerToShow | null {
  if (!layer.id) return null;
  if (layer.kind === 'internal') {
    return { kind: 'internal', id: layer.id };
  }
  const matched = catalogExternals.find((item) =>
    sameExternalLayerForAi(layer, item)
  );
  const id = matched?.id ?? layer.id;
  if (isWeatherCatalogSlugId(id)) return null;
  return { kind: 'external', id };
}

function resolveDeltaLayers(
  from: Array<Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>>,
  to: Array<Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>>,
  catalogExternals: Array<
    Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>
  >
): MapLayerToShow[] {
  const added = to.filter(
    (layer) => !from.some((existing) => sameExternalLayerForAi(existing, layer))
  );
  const seen = new Set<string>();
  const out: MapLayerToShow[] = [];
  for (const layer of added) {
    const resolved = resolveMapLayerId(layer, catalogExternals);
    if (!resolved) continue;
    const key = `${resolved.kind}:${resolved.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(resolved);
  }
  return out;
}

/** Newly checked layers that can be turned on on the map. Uncheck yields []. */
export function layersToShowOnMap(
  prev: Array<Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>>,
  next: Array<Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>>,
  catalogExternals: Array<
    Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>
  > = []
): MapLayerToShow[] {
  return resolveDeltaLayers(prev, next, catalogExternals);
}

/** Layers removed from the picker that can be turned off on the map. */
export function layersToHideOnMap(
  prev: Array<Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>>,
  next: Array<Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>>,
  catalogExternals: Array<
    Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>
  > = []
): MapLayerToShow[] {
  return resolveDeltaLayers(next, prev, catalogExternals);
}

export function selectedLayersSupportDatetimeFilter(
  layers: Array<{
    arcgisItemId?: string | null;
    name?: string | null;
    serviceUrl?: string | null;
  }>
): boolean {
  return layers.some(
    (layer) =>
      isFloodingBinsDemoLayer(layer) ||
      isTingOutagesTnFloodsLayer(layer) ||
      isUsaCurrentWildfiresLayer(layer)
  );
}

export function applyShowLayersOnMap(
  items: MapLayerToShow[],
  api: {
    isExternalLayerVisible: (id: string) => boolean;
    toggleExternalLayerVisibility: (id: string) => void;
    isInternalLayerVisible: (id: string) => boolean;
    toggleInternalLayerVisibility: (id: string) => void;
  }
): boolean {
  let turnedOn = false;
  for (const item of items) {
    if (item.kind === 'external') {
      if (!api.isExternalLayerVisible(item.id)) {
        api.toggleExternalLayerVisibility(item.id);
        turnedOn = true;
      }
    } else if (!api.isInternalLayerVisible(item.id)) {
      api.toggleInternalLayerVisibility(item.id);
      turnedOn = true;
    }
  }
  return turnedOn;
}

export function applyHideLayersOnMap(
  items: MapLayerToShow[],
  api: {
    isExternalLayerVisible: (id: string) => boolean;
    toggleExternalLayerVisibility: (id: string) => void;
    isInternalLayerVisible: (id: string) => boolean;
    toggleInternalLayerVisibility: (id: string) => void;
  }
): boolean {
  let turnedOff = false;
  for (const item of items) {
    if (item.kind === 'external') {
      if (api.isExternalLayerVisible(item.id)) {
        api.toggleExternalLayerVisibility(item.id);
        turnedOff = true;
      }
    } else if (api.isInternalLayerVisible(item.id)) {
      api.toggleInternalLayerVisibility(item.id);
      turnedOff = true;
    }
  }
  return turnedOff;
}

export function selectedLayerForAIKey(
  layer: Pick<SelectedLayerForAI, 'kind' | 'id' | 'attributeFilters'>
): string {
  const base = `${layer.kind}:${layer.id}`;
  const filter = layer.attributeFilters?.[0];
  if (!filter) return base;
  return `${base}:${filter.field}:${String(filter.value)}`;
}

function withCurrentTimeFilter(
  layer: SelectedLayerForAI,
  timeFiltersByLayerId?: Record<string, ExternalLayerTimeFilter>
): SelectedLayerForAI {
  const filter = timeFiltersByLayerId?.[layer.id];
  if (!filter) return layer;
  const timeFilterField = resolveTimeFilterField(layer);
  return {
    ...layer,
    timeExtent: {
      startIso: filter.startIso,
      endIso: filter.endIso,
    },
    timeFilterLabel: filter.label,
    ...(timeFilterField ? { timeFilterField } : {}),
  };
}

export function aiLayerPickPayloadToSelectedLayer(
  payload: AiLayerPickPayload,
  timeFiltersByLayerId?: Record<string, ExternalLayerTimeFilter>
): SelectedLayerForAI {
  const layer: SelectedLayerForAI = {
    kind: payload.kind,
    id: payload.id,
    name: payload.name,
    ...(payload.versionId ? { versionId: payload.versionId } : {}),
    ...(payload.serviceUrl !== undefined
      ? { serviceUrl: payload.serviceUrl }
      : {}),
    ...(payload.arcgisItemId !== undefined
      ? { arcgisItemId: payload.arcgisItemId }
      : {}),
    ...(payload.attributeFilters?.length
      ? { attributeFilters: payload.attributeFilters }
      : {}),
    ...(payload.legendFilterLabel
      ? { legendFilterLabel: payload.legendFilterLabel }
      : {}),
    ...(payload.spatialFilter ? { spatialFilter: payload.spatialFilter } : {}),
    ...(payload.spatialFilterLabel
      ? { spatialFilterLabel: payload.spatialFilterLabel }
      : {}),
    ...(payload.pickedFeatureName
      ? { pickedFeatureName: payload.pickedFeatureName }
      : {}),
    ...(payload.featureSnapshot
      ? { featureSnapshot: payload.featureSnapshot }
      : {}),
    ...(payload.featureSnapshotCoverage
      ? { featureSnapshotCoverage: payload.featureSnapshotCoverage }
      : {}),
  };
  return withCurrentTimeFilter(layer, timeFiltersByLayerId);
}

/**
 * Ensure a Layers datasource exists and upsert the picked layer. Returns
 * whether a new entry was added (false if already present).
 */
export function upsertSelectedLayerForAI(
  selections: unknown[],
  payload: AiLayerPickPayload,
  timeFiltersByLayerId?: Record<string, ExternalLayerTimeFilter>
): { next: unknown[]; added: boolean } {
  const safe = Array.isArray(selections) ? [...selections] : [];
  const nextLayer = aiLayerPickPayloadToSelectedLayer(
    payload,
    timeFiltersByLayerId
  );
  const key = selectedLayerForAIKey(nextLayer);

  const layersIdx = safe.findIndex(
    (sel) =>
      sel &&
      typeof sel === 'object' &&
      (sel as { source?: string }).source === 'Layers'
  );

  if (layersIdx < 0) {
    safe.push({
      source: 'Layers',
      id: '',
      selectedLayers: [nextLayer],
    } satisfies LayersRealtimeSelection);
    return { next: safe, added: true };
  }

  const existing = safe[layersIdx] as LayersRealtimeSelection;
  let selectedLayers = Array.isArray(existing.selectedLayers)
    ? existing.selectedLayers
    : [];

  // One active scope per layer: full-layer and legend-class picks replace each
  // other (and duplicates) for the same kind:id.
  selectedLayers = selectedLayers.filter(
    (l) => !(l.kind === nextLayer.kind && l.id === nextLayer.id)
  );

  if (selectedLayers.some((l) => selectedLayerForAIKey(l) === key)) {
    safe[layersIdx] = {
      ...existing,
      source: 'Layers',
      id: existing.id ?? '',
      selectedLayers,
    };
    return { next: safe, added: false };
  }

  safe[layersIdx] = {
    ...existing,
    source: 'Layers',
    id: existing.id ?? '',
    selectedLayers: [...selectedLayers, nextLayer],
  };
  return { next: safe, added: true };
}

export function stampSpatialFilter(
  layer: SelectedLayerForAI,
  payload: AiDrawnAreaPayload
): SelectedLayerForAI {
  return {
    ...layer,
    spatialFilter: payload.spatialFilter,
    spatialFilterLabel: payload.spatialFilterLabel ?? 'Area on map',
  };
}

export function stampSpatialFilterOnLayers(
  layers: SelectedLayerForAI[],
  payload: AiDrawnAreaPayload
): SelectedLayerForAI[] {
  return layers.map((layer) => stampSpatialFilter(layer, payload));
}

/**
 * Stamp a drawn-area spatialFilter on existing Layers selections, or add
 * currently visible map layers with that filter when none are selected.
 */
export function applyDrawnAreaToLayersSelection(
  selections: unknown[],
  payload: AiDrawnAreaPayload,
  visibleLayers: AiLayerPickPayload[],
  timeFiltersByLayerId?: Record<string, ExternalLayerTimeFilter>
): { next: unknown[]; added: boolean } {
  const safe = Array.isArray(selections) ? [...selections] : [];
  const layersIdx = safe.findIndex(
    (sel) =>
      sel &&
      typeof sel === 'object' &&
      (sel as { source?: string }).source === 'Layers'
  );

  if (layersIdx >= 0) {
    const existing = safe[layersIdx] as LayersRealtimeSelection;
    const selectedLayers = Array.isArray(existing.selectedLayers)
      ? existing.selectedLayers
      : [];
    if (selectedLayers.length > 0) {
      safe[layersIdx] = {
        ...existing,
        source: 'Layers',
        id: existing.id ?? '',
        selectedLayers: selectedLayers.map((layer) =>
          stampSpatialFilter(layer, payload)
        ),
      };
      return { next: safe, added: true };
    }
  }

  if (visibleLayers.length === 0) {
    return { next: safe, added: false };
  }

  let next = safe;
  for (const layer of visibleLayers) {
    const result = upsertSelectedLayerForAI(
      next,
      {
        ...layer,
        spatialFilter: payload.spatialFilter,
        spatialFilterLabel: payload.spatialFilterLabel ?? 'Area on map',
      },
      timeFiltersByLayerId
    );
    next = result.next;
  }
  return { next, added: true };
}

export function getLayersAiMapScopeLabel(selections: unknown[]): string | null {
  if (!Array.isArray(selections)) return null;
  const layersSel = selections.find(
    (sel) =>
      sel &&
      typeof sel === 'object' &&
      (sel as { source?: string }).source === 'Layers'
  ) as LayersRealtimeSelection | undefined;
  if (!layersSel || !Array.isArray(layersSel.selectedLayers)) return null;
  return getAiMapScopeLabel(layersSel.selectedLayers);
}

/** Remove map-pick scope (spatialFilter / clicked-feature snapshot) only. */
export function clearAiMapScopeFromSelections(
  selections: unknown[]
): unknown[] {
  if (!Array.isArray(selections)) return selections;
  return selections.map((sel) => {
    if (!sel || typeof sel !== 'object') return sel;
    const entry = sel as LayersRealtimeSelection;
    if (entry.source !== 'Layers' || !Array.isArray(entry.selectedLayers)) {
      return sel;
    }
    return {
      ...entry,
      selectedLayers: entry.selectedLayers.map((layer) => {
        const {
          spatialFilter: _sf,
          spatialFilterLabel: _sfl,
          pickedFeatureName: _pfn,
          ...rest
        } = layer;
        const isFeaturePick =
          Boolean(layer.pickedFeatureName) ||
          (layer.spatialFilterLabel?.startsWith('Selected:') ?? false);
        if (!isFeaturePick) return rest;
        const {
          attributeFilters: _af,
          legendFilterLabel: _lfl,
          featureSnapshot: _snap,
          featureSnapshotCoverage: _cov,
          ...withoutFeature
        } = rest;
        return withoutFeature;
      }),
    };
  });
}
